import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

interface HelpDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function HelpDialog({ open, onOpenChange }: HelpDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">

                {/* Sticky Header */}
                <DialogHeader >
                    <DialogTitle className="text-2xl font-semibold">
                        Rules Engine Microservice
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 text-sm text-gray-700 leading-relaxed">
                    <h2 className="text-lg font-semibold text-black">Rules Manager</h2>

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Creating Rules</h3>
                        <p>The user can add a rule by clicking the <span className="font-medium">Add Rules</span> button.</p>

                        <ol className="space-y-2 ml-1">
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 1 –</span>
                                <span>To add a rule, the user needs to provide a <span className="font-medium">Rule Name</span>. The user can also copy a rule from the Rules Repository by providing a new name.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 2 –</span>
                                <span>Once the basic details are added, click the <span className="font-medium">Plus (+) icon</span> in the Action column. Multiple options will be displayed. Click the <span className="font-medium">Edit Builder</span> option.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 3 –</span>
                                <span>Select the <span className="font-medium">Rule Type</span> from the dropdown. Then add conditions within Group 1. To add a condition, select the <span className="font-medium">Data Type</span> and <span className="font-medium">Attribute</span> from the dropdowns.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 4 –</span>
                                <span>After adding the conditions, click the <span className="font-medium">Validate Script</span> button. The system will display whether the script is valid or not.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 5 –</span>
                                <span>Click <span className="font-medium">Save</span> to save the rule.</span>
                            </li>
                        </ol>

                        <div>
                            <p className="font-semibold text-black">Additional Information</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>The user can add multiple Groups and Sub-Groups using the <span className="font-medium">Add Group</span> and <span className="font-medium">Add Sub Group</span> buttons.</li>
                                <li>For the Pricing rule type, a link is available to make rule adjustments.</li>
                            </ul>
                        </div>
                    </section>

                    {/* APPROVE RULES */}
                    <section className="space-y-2">
                        <h3 className="text-base font-bold text-black">Approve Rules</h3>
                        <p>Once a rule is created, the user needs to approve the rule from the <span className="font-medium">Approve</span> option in the Action column.</p>
                    </section>

                    {/* EDIT RULE */}
                    <section className="space-y-2">
                        <h3 className="text-base font-bold text-black">Edit Rule</h3>
                        <p>Users can edit rules manually or using the <span className="font-medium">Edit with AI</span> option.</p>
                    </section>

                    {/* RULES FUNCTION */}
                    <h2 className="text-lg font-semibold text-black">Rules Function</h2>

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Creating Rules Function</h3>
                        <p>Users can add a rules function by clicking the <span className="font-medium">Add Function</span> button.</p>

                        <ol className="space-y-2 ml-1">
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 1 –</span>
                                <span>To create a rules function, provide the <span className="font-medium">Function Name</span> and <span className="font-medium">Parameters</span>.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 2 –</span>
                                <span>Once the function is added, approve the function using the <span className="font-medium">Action</span> button.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 3 –</span>
                                <span>The function will be configured from the backend.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">Step 4 –</span>
                                <span>After the rules function is configured, it will be available in the <span className="font-medium">Set Rule Expression</span> section within the Edit Builder of the Rules Manager.</span>
                            </li>
                        </ol>
                    </section>

                    {/* RULES VERSION */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Rules Version</h3>
                        <p>Approved rules from the Rules Manager will be available in the Rules Version.</p>
                        <ul className="list-disc ml-5 space-y-1">
                            <li>For newly approved rules, a <span className="font-medium">Create</span> option will be available in the Action column. Using the Create button, users can add rule values.</li>
                            <li>For existing rules, a <span className="font-medium">Modify</span> option will be available in the Action column to update rule values.</li>
                        </ul>

                        <div>
                            <p className="font-semibold text-black">Version History</p>
                            <ul className="list-disc ml-5 mt-1 space-y-1">
                                <li>After each modification, rules can either be saved as a new version or updated in the existing version.</li>
                                <li>When <span className="font-medium">Save as New Version</span> is selected, a new version is created and displayed in the Version History.</li>
                            </ul>
                        </div>
                    </section>

                    {/* RULES PUBLISH */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Rules Publish</h3>
                        <ul className="list-disc ml-5 space-y-1">
                            <li>The user can publish rules from this section. Once a rule is added or modified in the Rules Version, only that rule will be displayed in the Rules Publish section.</li>
                            <li>To publish a rule, click the <span className="font-medium">Publish</span> link in the Action column.</li>
                            <li>The user can also unpublish a rule from this section.</li>
                            <li>After every update or modification, the specific rule must be published again. First, unpublish the rule and then publish it.</li>
                        </ul>
                    </section>

                    {/* DATA QUALITY */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Data Quality</h3>
                        <p>The Data Quality module identifies and logs the following three types of issues:</p>
                        <ul className="space-y-2 ml-5">
                            <li>
                                <span className="font-semibold">Duplicate</span> – Identifies rows with the same Type and exactly the same Code Name.
                            </li>
                            <li>
                                <span className="font-semibold">Orphans</span> – Identifies codes that do not have a valid parent type reference.
                            </li>
                            <li>
                                <span className="font-semibold">Overlaps</span> – Identifies codes with the same name that exist under different types.
                            </li>
                        </ul>
                    </section>

                    {/* IMPORT / EXPORT */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Import / Export</h3>
                        <div>
                            <p className="font-semibold">Export</p>
                            <p className="mt-1">Rules can be exported in different formats and scopes.</p>
                            <p className="mt-1 font-medium">Available export scopes:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Rules</li>
                                <li>Rules Function</li>
                                <li>Rules Version</li>
                                <li>Rules Publish</li>
                            </ul>
                        </div>
                    </section>

                    {/* AUDIT LOG */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Audit Log</h3>
                        <p>The Audit Log records all system errors related to the Rules Manager, including:</p>
                        <ul className="list-disc ml-5 space-y-0.5">
                            <li>Validation errors</li>
                            <li>Authentication errors</li>
                            <li>Database errors</li>
                        </ul>
                        <div>
                            <p className="font-medium mt-2">Each log entry includes:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>IP address</li>
                                <li>Status code</li>
                                <li>Error timestamp</li>
                                <li>Error type</li>
                            </ul>
                        </div>
                    </section>

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">AI/NLPS Console</h3>

                        <div className="bg-gray-100 p-3 rounded text-xs space-y-2 max-h-64 overflow-y-auto">
                            <p className="font-semibold mb-1">Example Commands</p>
                            <p>Create a rule where a is less than b and c is less than d</p>
                            <p>Create a rule where a is less than b and c is equal to 1</p>
                            <p>Create a rule where a is less than b and c is less than value d or create a rule where a is less than b and c is less than d. d is a value</p>
                            <p>Create a rule where a is less than b and c is equal to 1 or d is not equal to e, group them then a is equal to 100</p>
                        </div>
                    </section>

                </div>
            </DialogContent>
        </Dialog>
    );
}